import React, { useState } from "react";
import { Spinner, BypassEmailGate, authScreens } from "@jkast90/kast-common/ui";
import {
  RequireAuth,
  RequireGroup,
  hasGroup,
  useSession,
} from "@jkast90/kast-common/auth";
import { useAuth, bypassEmail } from "../hooks/useAuth";
import { ADMIN_GROUPS } from "../../_common/adminMode";
import { useAdminVerification } from "../../_common/queries";
import { getAuthEnabled } from "../../_common/settings";
import { signOut } from "../signIn";
import { signInScreen } from "../signInScreen";

interface Props {
  children: React.ReactNode;
  /** The page needs admin: the family-wide `admin` or `kindly-admin`. */
  requireGroup?: "admin";
}

function Checking({ what }: { what: string }) {
  return (
    <div className="routeFallback">
      <Spinner showLabel label={what} />
    </div>
  );
}

/**
 * The sign-in gate (standard 7), over the shared session's RequireAuth.
 * While the session is unknown it shows a neutral wait, never the page (whose
 * empty lists would read as "nothing here"). Signed out, or once the session
 * ends (a rejected refresh), it goes to sign-in, set up to return here, and
 * shows the sign-in prompt meanwhile. A page that needs a group checks the
 * token's groups, then waits for the server's answer (RequireGroup's
 * `verify`). With auth off (local dev) the bypass email stands in for the
 * signed-in user and the API decides.
 */
export default function ProtectedRoute({ children, requireGroup }: Props) {
  // Read once on mount: the auth settings only change with a reload.
  const [authEnabled] = useState(getAuthEnabled);
  const { isAuthenticated } = useAuth();
  const state = useSession();
  // The server is asked only of a member the token's groups already let in.
  const admin = useAdminVerification(
    isAuthenticated && !!requireGroup && hasGroup(state, ADMIN_GROUPS),
  );

  const screens = authScreens({
    signIn: { ...signInScreen(), autoSignIn: true },
    signInEnded: {
      title: "Your session has ended",
      detail: "Sign in again to pick up where you left off.",
    },
    notAllowed: {
      group: requireGroup,
      title: "You don't have access to this page",
      homeHref: "/",
      onSwitchAccount: () => void signOut(),
      switchLabel: "Sign Out",
    },
    loading: <Checking what="Checking your session…" />,
    checking: <Checking what="Checking your access…" />,
  });
  const whenAuthOff = (
    <BypassEmailGate
      store={bypassEmail}
      label="Email Address:"
      descriptionPosition="after"
    >
      {children}
    </BypassEmailGate>
  );

  if (!requireGroup) {
    return (
      <RequireAuth
        authEnabled={authEnabled}
        whenAuthOff={whenAuthOff}
        {...screens}
      >
        {children}
      </RequireAuth>
    );
  }
  return (
    <RequireGroup
      group={ADMIN_GROUPS}
      verify={{
        verified: admin.data ?? null,
        failed: admin.isError,
        retry: () => void admin.refetch(),
      }}
      authEnabled={authEnabled}
      whenAuthOff={whenAuthOff}
      {...screens}
    >
      {children}
    </RequireGroup>
  );
}
